import {styled} from '@mui/material/styles'
import {
  createContext,
  memo,
  type PropsWithChildren,
  type ReactElement,
  use,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useEffectEvent} from '../hooks/useEffectEvent'

const StyledSwiper = memo(styled('div')`
    overflow: hidden;
    width: 100%;
    height: 100%;

    > .swiper-slides {
        width: 100%;
        height: 100%;
        display: flex;

        > .swiper-slide {
            position: relative;
            width: 100%;
            height: 100%;
            flex-shrink: 0;
            transform: translateZ(0);
            overflow-x: hidden;
            overflow-y: auto;
        }
    }
`)

const settings = {
  threshold: 5,
  touchAngle: 45,
  longSwipesMs: 300,
  longSwipesRatio: 0.5,
  animationDuration: 300,
  edgeSwipeThreshold: 20,
}

const SwiperAncestryContext = createContext(false)
const useSwiperAncestry = () => use(SwiperAncestryContext)

interface LazySlideProps extends PropsWithChildren {
  render: boolean
}

const LazySlide = memo(({children, render}: LazySlideProps) => {
  const rendered = useRef<boolean>(false)
  rendered.current ||= render

  if (rendered.current) {
    return children
  }

  return null
})

export interface SwiperProps {
  slides: ReactElement[]
  slideIndex?: number
  onSlideChange?: (index: number) => void
  autoplay?: boolean
}

export const Swiper = memo(({slides, slideIndex, onSlideChange, autoplay = false}: SwiperProps) => {
  const swiperRef = useRef<HTMLDivElement>(null)
  const slidesRef = useRef<Map<string, HTMLDivElement | null>>(new Map())
  const currentSlidePosition = useRef(0)
  const dragStart = useRef<[number, number, number]>([0, 0, Date.now()])
  const isScrolling = useRef<undefined | boolean>(undefined)
  const currentSlide = useRef(slideIndex ?? 0)
  const slideCount = useRef(slides.length)
  const isNested = useRef(false)
  const [nextIndex, setNextIndex] = useState<number>(slideIndex ?? 0)
  isNested.current = useSwiperAncestry()
  slideCount.current = slides.length

  const slideKeys = useMemo(() => slides.map((slide, index) => slide.key ?? `s-${index}`), [slides])
  useEffect(() => {
    for (const key of [...slidesRef.current.keys()].filter((key: string) => !slideKeys.includes(key))) {
      slidesRef.current.delete(key)
    }
  }, [slideKeys])

  const getSlide = useEffectEvent((index: number) => {
    const slideKey = slideKeys[index]
    if (!slideKey) {
      return null
    }
    return slidesRef.current.get(slideKey) ?? null
  })
  const getSlidePosition = useEffectEvent((index: number) => {
    const children = getSlide(index)
    if (!children) {
      return 0
    }

    return children.offsetLeft
  })
  const changeSlide = useEffectEvent((changedValue) => {
    isScrolling.current = undefined

    const newValue = Math.max(0, Math.min(slideCount.current - 1, changedValue))
    if (currentSlide.current !== newValue) {
      currentSlide.current = newValue
      currentSlidePosition.current = getSlidePosition(currentSlide.current)
    }
    if (swiperRef.current) {
      swiperRef.current.style.transitionDuration = `${settings.animationDuration}ms`
      swiperRef.current.style.transform = `translateX(-${currentSlidePosition.current}px)`
    }
    if (currentSlide.current !== slideIndex) {
      onSlideChange?.(currentSlide.current)
    }
  })

  useEffect(() => {
    if (!autoplay || slideCount.current <= 1) {
      return
    }
    const timer = setInterval(() => {
      changeSlide((currentSlide.current + 1) % slideCount.current)
    }, 2500)

    return () => {
      clearInterval(timer)
    }
  }, [autoplay, changeSlide])

  useEffect(() => {
    if (slideIndex !== undefined) {
      changeSlide(slideIndex)
    }
  }, [slideIndex, changeSlide])

  useLayoutEffect(() => {
    const ref = swiperRef.current
    if (!ref) {
      return
    }
    const getPosition = (event: TouchEvent): [number, number, number] => {
      return [event.changedTouches[0]?.clientX ?? 0, event.changedTouches[0]?.clientY ?? 0, Date.now()]
    }
    const observer = new ResizeObserver(() => {
      currentSlidePosition.current = getSlidePosition(currentSlide.current)
      if (!swiperRef.current) {
        return
      }
      swiperRef.current.style.transitionDuration = '0ms'
      swiperRef.current.style.transform = `translateX(-${currentSlidePosition.current}px)`
    })
    const handleTouchStart = (event: TouchEvent) => {
      if (!event.targetTouches[0]) {
        return
      }
      // prevent edge swipe
      const startX = event.targetTouches[0].pageX
      if (startX <= settings.edgeSwipeThreshold || startX >= window.innerWidth - settings.edgeSwipeThreshold) {
        isScrolling.current = true
        return
      }
      dragStart.current = getPosition(event)
      isScrolling.current = undefined
    }
    const handleTouchEnd = (event: TouchEvent) => {
      if (isScrolling.current !== false) {
        return
      }
      isScrolling.current = undefined
      const position = getPosition(event)
      if (position[2] - dragStart.current[2] > settings.longSwipesMs) {
        // @ts-ignore
        const longSwipesWidth = swiperRef.current?.children[currentSlide.current].offsetWidth * settings.longSwipesRatio
        if (swiperRef.current && Math.abs(dragStart.current[0] - position[0]) < longSwipesWidth) {
          changeSlide(currentSlide.current)
          return
        }
      }
      if (dragStart.current[0] < position[0]) {
        changeSlide(currentSlide.current - 1)
      } else {
        changeSlide(currentSlide.current + 1)
      }
    }
    const handleTouchMove = (event: TouchEvent) => {
      if (!swiperRef.current || isScrolling.current) {
        return
      }
      if (event.touches.length >= 2 || !event.changedTouches[0]) {
        return
      }

      const position = [event.changedTouches[0].clientX, event.changedTouches[0].clientY] as const
      if (isScrolling.current === undefined) {
        const diff = [
          dragStart.current[0] - position[0],
          dragStart.current[1] - position[1],
        ] as const
        const dist = Math.sqrt(diff[0] ** 2 + diff[1] ** 2)
        if (dist < settings.threshold) {
          return
        }

        const angle = (Math.atan2(Math.abs(diff[1]), Math.abs(diff[0])) * 180) / Math.PI
        isScrolling.current = angle > settings.touchAngle
        if (angle > settings.touchAngle) {
          return
        }

        swiperRef.current.style.transitionDuration = '0ms'
      }

      const diff = position[0] - dragStart.current[0]
      const idEdge = diff > 0 ? currentSlide.current <= 0 : currentSlide.current >= slideCount.current - 1

      if (isNested.current && idEdge) {
        isScrolling.current = true
        return
      }

      event.preventDefault()
      event.stopPropagation()
      const ratio = idEdge ? 0.5 : 1

      setNextIndex(currentSlide.current + (diff < 0 ? 1 : -1))
      swiperRef.current.style.transform = `translateX(${diff * ratio - currentSlidePosition.current}px)`
    }

    observer.observe(ref)
    const controller = new AbortController()
    ref.addEventListener('touchstart', handleTouchStart, {passive: true, signal: controller.signal})
    ref.addEventListener('touchend', handleTouchEnd, {passive: true, signal: controller.signal})
    ref.addEventListener('touchmove', handleTouchMove, {signal: controller.signal})

    return () => {
      observer.disconnect()
      controller.abort()
    }
  }, [changeSlide, getSlidePosition])
  const registerRef = useEffectEvent((index, el: HTMLDivElement | null) => {
    slidesRef.current.set(slideKeys[index] ?? '0', el)
  })

  return <SwiperAncestryContext value={true}>
    <StyledSwiper className="swiper-container">
      <div ref={swiperRef} className="swiper-slides">
        {slides.map((slide, index) => <div
          className="swiper-slide"
          key={slideKeys[index]}
          ref={(el) => registerRef(index, el)}
        >
          <LazySlide render={slideIndex === undefined || index === slideIndex || index === nextIndex}>{slide}</LazySlide>
        </div>)}
      </div>
    </StyledSwiper>
  </SwiperAncestryContext>
})
